import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { loadJSON, saveJSON } from '../../utils/tabStorage'
import { matchScore } from '../../utils/search'
import { topicLabelFor } from '../../utils/topicLabel'
import { deriveTopicView } from './topicTree'

export const TOPICS_NAV_KEY = 'ct-topics-nav-v1'

const HISTORY_LIMIT = 50

function defaultNav() {
  return {
    mode: 'segmented',
    trailIds: [],
    collapsed: [],
    topicId: null,
    query: '',
    history: [],
    historyIndex: -1,
  }
}

function topicSnap(topic) {
  if (!topic?._id) return null
  return { id: String(topic._id), name: topic.name || '' }
}

function coerceHistory(raw, topicId) {
  const history = Array.isArray(raw?.history)
    ? raw.history
        .filter((entry) => entry && entry.id)
        .map((entry) => ({ id: String(entry.id), name: entry.name || '' }))
        .slice(-HISTORY_LIMIT)
    : []
  if (!history.length && topicId) history.push({ id: String(topicId), name: '' })
  let historyIndex = Number.isInteger(raw?.historyIndex) ? raw.historyIndex : history.length - 1
  if (!history.length) historyIndex = -1
  else if (historyIndex >= history.length) historyIndex = history.length - 1
  else if (historyIndex < 0) historyIndex = 0
  return { history, historyIndex }
}

function pushTopicHistory(nav, topic) {
  const snap = topicSnap(topic)
  if (!snap) return { ...nav, topicId: null }
  const history = Array.isArray(nav.history) ? nav.history : []
  const index = Number.isInteger(nav.historyIndex) ? nav.historyIndex : -1
  if (history[index] && history[index].id === snap.id) {
    return String(nav.topicId) === snap.id ? nav : { ...nav, topicId: snap.id }
  }
  let next = history.slice(0, index + 1)
  next.push(snap)
  if (next.length > HISTORY_LIMIT) next = next.slice(next.length - HISTORY_LIMIT)
  return { ...nav, topicId: snap.id, history: next, historyIndex: next.length - 1 }
}

function stepTopicHistory(nav, dir) {
  const history = Array.isArray(nav.history) ? nav.history : []
  const index = Number.isInteger(nav.historyIndex) ? nav.historyIndex : -1
  const nextIndex = index + dir
  if (nextIndex < 0 || nextIndex >= history.length) return nav
  return { ...nav, historyIndex: nextIndex, topicId: history[nextIndex].id }
}

function dropTopicHistory(nav, key) {
  const old = Array.isArray(nav.history) ? nav.history : []
  const index = Number.isInteger(nav.historyIndex) ? nav.historyIndex : -1
  const history = old.filter((entry) => entry.id !== key)
  let historyIndex = -1
  if (history.length) {
    if (old[index] && old[index].id === key) {
      for (let i = index - 1; i >= 0; i -= 1) {
        if (old[i].id === key) continue
        historyIndex = old.slice(0, i + 1).filter((entry) => entry.id !== key).length - 1
        break
      }
      if (historyIndex < 0) historyIndex = 0
    } else {
      historyIndex = Math.max(
        0,
        old.slice(0, index + 1).filter((entry) => entry.id !== key).length - 1
      )
    }
    if (historyIndex >= history.length) historyIndex = history.length - 1
  }
  const topicId =
    nav.topicId && String(nav.topicId) === key
      ? history[historyIndex]?.id || null
      : nav.topicId
  return { ...nav, history, historyIndex, topicId }
}

function coerceNav(raw) {
  const base = defaultNav()
  if (!raw || typeof raw !== 'object') return base
  const topicId = raw.topicId ? String(raw.topicId) : null
  return {
    mode: raw.mode === 'collapsed' ? 'collapsed' : 'segmented',
    trailIds: Array.isArray(raw.trailIds) ? raw.trailIds.map(String) : [],
    collapsed: Array.isArray(raw.collapsed) ? raw.collapsed.map(String) : [],
    topicId,
    query: typeof raw.query === 'string' ? raw.query : '',
    ...coerceHistory(raw, topicId),
  }
}

function loadNavMap() {
  try {
    const parsed = loadJSON(TOPICS_NAV_KEY)
    if (!parsed || typeof parsed !== 'object') return {}
    const out = {}
    Object.keys(parsed).forEach((id) => {
      out[id] = coerceNav(parsed[id])
    })
    return out
  } catch {
    return {}
  }
}

function collectWindowIds(columns) {
  const ids = []
  columns.forEach((col) => col.windows.forEach((win) => ids.push(win.id)))
  return ids
}

// One browse position per window: drill trail, collapsed folds, view mode, and
// the topic whose bookmarks the window shows. The sidebar always mirrors the
// active window.
export default function useTopicsWorkspace(grid, topics) {
  const activeId = grid.activeId
  const [navById, setNavById] = useState(loadNavMap)
  const [revealSignal, setRevealSignal] = useState({ id: null, nonce: 0 })
  const topicsRef = useRef(topics)
  const navRef = useRef(navById)
  topicsRef.current = topics
  navRef.current = navById

  const timerRef = useRef(null)
  useEffect(() => {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      try {
        saveJSON(TOPICS_NAV_KEY, navById)
      } catch {
        /* ignore quota / privacy-mode errors */
      }
    }, 150)
    return () => clearTimeout(timerRef.current)
  }, [navById])

  useEffect(() => {
    const ids = collectWindowIds(grid.columns)
    setNavById((prev) => {
      let changed = false
      const next = {}
      ids.forEach((id) => {
        if (prev[id]) next[id] = prev[id]
        else {
          next[id] = defaultNav()
          changed = true
        }
      })
      if (!changed && Object.keys(prev).length !== ids.length) changed = true
      return changed ? next : prev
    })
  }, [grid.columns])

  const nav = navById[activeId] || defaultNav()
  const labelOf = useMemo(() => topicLabelFor(topics), [topics])
  const view = useMemo(() => {
    const query = String(nav.query || '').trim()
    const matches = query ? (topic) => matchScore(query, labelOf(topic)) != null : null
    return deriveTopicView({ nav, topics, matches })
  }, [nav, topics, labelOf])

  // A move can leave a window drilled into a topic that is no longer a child
  // of that trail. Drop the broken tail once the topic list is loaded.
  useEffect(() => {
    if (!topics.length) return undefined
    setNavById((prev) => {
      let changed = false
      const next = {}
      Object.keys(prev).forEach((winId) => {
        const entry = prev[winId]
        const derived = deriveTopicView({ nav: entry, topics })
        if (!derived.trailBroken) {
          next[winId] = entry
          return
        }
        changed = true
        next[winId] = { ...entry, trailIds: derived.trail.map((topic) => String(topic._id)) }
      })
      return changed ? next : prev
    })
    return undefined
  }, [topics])

  const patchNav = useCallback(
    (updater) => {
      if (!activeId) return
      setNavById((prev) => {
        const cur = prev[activeId] || defaultNav()
        const nextNav = typeof updater === 'function' ? updater(cur) : { ...cur, ...updater }
        return { ...prev, [activeId]: nextNav }
      })
    },
    [activeId]
  )

  const patchNavFor = useCallback((windowId, updater) => {
    if (!windowId) return
    setNavById((prev) => {
      const cur = prev[windowId] || defaultNav()
      const nextNav = typeof updater === 'function' ? updater(cur) : { ...cur, ...updater }
      return { ...prev, [windowId]: nextNav }
    })
  }, [])

  const select = useCallback(
    (topic) => patchNav((cur) => pushTopicHistory(cur, topic)),
    [patchNav]
  )
  const stepHistory = useCallback(
    (windowId, dir) => patchNavFor(windowId, (cur) => stepTopicHistory(cur, dir)),
    [patchNavFor]
  )
  // Show the window's open topic as a row in the sidebar: drill to its parent
  // (or return to the root in the collapsed tree), unfold its ancestors, clear
  // the search, and ask the list to scroll to it.
  const revealTopic = useCallback((windowId) => {
    const cur = navRef.current[windowId]
    const topicId = cur?.topicId
    if (!windowId || !topicId) return
    const topic = topicsRef.current.find((entry) => String(entry._id) === String(topicId))
    if (!topic) return
    const ancestorIds = (topic.path || []).map((id) => {
      if (id && typeof id === 'object') return String(id._id || '')
      return String(id || '')
    }).filter(Boolean)
    patchNavFor(windowId, (nav) => {
      const collapsed = new Set((nav.collapsed || []).map(String))
      ancestorIds.forEach((id) => collapsed.delete(id))
      return {
        ...nav,
        query: '',
        collapsed: [...collapsed],
        trailIds: nav.mode === 'collapsed' ? [] : ancestorIds,
      }
    })
    setRevealSignal((signal) => ({ id: String(topic._id), nonce: signal.nonce + 1 }))
  }, [patchNavFor])
  const drillInto = useCallback(
    (topic) => {
      if (!topic?._id) return
      patchNav((cur) => ({ ...cur, trailIds: [...cur.trailIds, String(topic._id)] }))
    },
    [patchNav]
  )
  // index -1 is the forest root. A later index keeps that many drilled topics.
  const goTo = useCallback(
    (index) => {
      if (index < 0) {
        patchNav((cur) => ({ ...cur, trailIds: [] }))
        return
      }
      patchNav((cur) => ({ ...cur, trailIds: cur.trailIds.slice(0, index + 1) }))
    },
    [patchNav]
  )
  const setMode = useCallback((mode) => patchNav({ mode }), [patchNav])
  const setQuery = useCallback((query) => patchNav({ query: String(query || '') }), [patchNav])
  const forgetTopic = useCallback((id) => {
    const key = String(id)
    setNavById((prev) => {
      const next = {}
      Object.keys(prev).forEach((winId) => {
        const nav = prev[winId]
        const cleared = dropTopicHistory(nav, key)
        next[winId] = {
          ...cleared,
          trailIds: (cleared.trailIds || []).filter((topicId) => String(topicId) !== key),
          collapsed: (cleared.collapsed || []).filter((topicId) => String(topicId) !== key),
        }
      })
      return next
    })
  }, [])
  const toggleCollapse = useCallback(
    (topic) =>
      patchNav((cur) => {
        const id = String(topic._id)
        const set = new Set(cur.collapsed)
        if (set.has(id)) set.delete(id)
        else set.add(id)
        return { ...cur, collapsed: [...set] }
      }),
    [patchNav]
  )

  const depthOf = useCallback(
    (topic) => {
      if (view.mode !== 'collapsed') return 0
      const base = view.current ? (view.current.level || 0) + 1 : 0
      return Math.max(0, (topic.level || 0) - base)
    },
    [view]
  )
  const isExpanded = useCallback(
    (topic) => !view.collapsedSet.has(String(topic._id)),
    [view]
  )

  return {
    navById,
    mode: view.mode,
    atRoot: view.atRoot,
    trail: view.trail,
    rows: view.rows,
    current: view.current,
    selectedId: nav.topicId,
    query: nav.query || '',
    scope: view.scope,
    hasChildren: view.hasChildren,
    depthOf,
    isExpanded,
    isDimmed: view.isDimmed,
    filterActive: view.filterActive,
    select,
    stepHistory,
    revealTopic,
    revealSignal,
    drillInto,
    goTo,
    setMode,
    setQuery,
    forgetTopic,
    toggleCollapse,
  }
}
